import { useTimeZone } from '@/auth/useTimeZone.js';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary.js';
import { OnDemandGLALink } from '@/components/session/on-demand-gla-link.js';
import { BaseEntityTableGroupKey, EntityTable, EntityTableGroupBy } from '@/components/table/entity-table.js';
import { Button } from '@/components/ui/button.js';
import { RightDrawer } from '@/components/ui/right-drawer.js';
import { airServerErrorFrom, axiosErrorFrom } from '@/lib/error-handling.js';
import { stringIfNull } from '@/lib/string-utils.js';
import {
  formatInstant,
  formatTimeRange,
  getNeedAssessmentLabelForMakeupDiagnostic,
  isMakeupDiagnosticLesson,
} from '@airreading/common';
import { CoachViewableSessionFeedback, studentYearOption, toSessionFeedbackCompoundId } from '@airreading/common/data';
import { createColumnHelper, getCoreRowModel, TableOptions, useReactTable } from '@tanstack/react-table';
import { ClockIcon, Maximize2, PencilIcon, WallpaperIcon, X } from 'lucide-react';
import { cn } from '@/lib/tailwind-utils.js';
import { ComponentProps, useCallback, useMemo, useRef, useState } from 'react';
import { SessionFeedbackStatus } from '../session/session-feedback-status.js';
import { CoachViewableSessionFeedbackPage, SaveAndCloseHandle } from './coach-viewable-session-feedback-page.js';
import { TimeZoneTruncated } from '@/components/time-zone-truncated.js';
import { StudentNeedsIndicators } from '@/components/student-needs-indicators.js';

type EntityTableProps = ComponentProps<typeof EntityTable>;

interface CoachViewableFeedbacksTableProps<GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey> {
  sessionFeedbacks: CoachViewableSessionFeedback[];
  filters?: EntityTableProps['filters'];
  filterBarClassName?: EntityTableProps['filterBarClassName'];
  filterLeftClassName?: EntityTableProps['filterLeftClassName'];
  emptyState?: EntityTableProps['emptyState'];
  tableHookProps?: Omit<TableOptions<CoachViewableSessionFeedback>, 'columns' | 'data' | 'getCoreRowModel'>;
  isLoading?: boolean;
  showPagination?: boolean;
  groupBy?: EntityTableGroupBy<CoachViewableSessionFeedback, GroupKey>;
}

export const CoachViewableFeedbacksTableColumnIds = {
  feedbackStatus: 'feedbackStatus',
  viewDetailsCoach: 'viewDetailsCoach',
  time: 'time',
  student: 'student',
  lessonName: 'lessonName',
  schoolDistrict: 'schoolDistrict',
  grade: 'grade',
  viewDrawer: 'viewDrawer',
} as const;

const columnHelper = createColumnHelper<CoachViewableSessionFeedback>();

const createCoachViewableFeedbacksColumns = (
  displayTimeZone: string,
  onSelectFeedback: (feedback: CoachViewableSessionFeedback) => void,
) => [
  columnHelper.accessor((row) => row, {
    id: CoachViewableFeedbacksTableColumnIds.viewDetailsCoach,
    header: '',
    cell: ({ getValue, row }) => {
      const feedback = getValue();
      const rowIndex = row.index;

      return (
        <div className="flex h-12 w-12 shrink-0 items-center justify-center">
          <Button
            variant="ghost"
            className="h-12 w-12 shrink-0 p-0"
            onClick={() => onSelectFeedback(feedback)}
            data-testid={`feedback-row-button-edit-${rowIndex + 1}`}
          >
            <PencilIcon size={16} />
          </Button>
        </div>
      );
    },
    size: 50,
  }),
  columnHelper.accessor((row) => row, {
    id: CoachViewableFeedbacksTableColumnIds.feedbackStatus,
    header: 'Feedback status',
    cell: ({ getValue, row }) => {
      const feedback = getValue();
      const rowNumber = row.index + 1;
      const {
        session: { hoursCanceledBeforeStart },
      } = feedback;
      return (
        <SessionFeedbackStatus
          feedbacks={[feedback]}
          hoursCanceledBeforeStart={hoursCanceledBeforeStart}
          lateSentSuffixLabel="(late)"
          testId={`feedback-row-cell-status-${rowNumber}`}
        />
      );
    },
  }),
  columnHelper.accessor('session', {
    id: CoachViewableFeedbacksTableColumnIds.time,
    header: () => (
      <span className="flex items-center whitespace-nowrap">
        Time (
        <TimeZoneTruncated timeZone={displayTimeZone} />)
      </span>
    ),
    cell: ({ getValue, row }) => {
      const session = getValue();
      const rowNumber = row.index + 1;
      return (
        <span data-testid={`feedback-row-cell-time-${rowNumber}`}>
          {formatInstant(session.startInstant, displayTimeZone).formattedDateWithoutYear},{' '}
          {formatTimeRange(session.startInstant, session.endInstant, displayTimeZone)}
        </span>
      );
    },
  }),
  columnHelper.accessor((row) => row, {
    id: CoachViewableFeedbacksTableColumnIds.student,
    header: 'Student',
    cell: ({ getValue, row }) => {
      const feedback = getValue();
      const rowNumber = row.index + 1;
      const { student, session, onDemandGLA } = feedback;
      const name = student.coachFacingDisplayName ?? '';
      const label = isMakeupDiagnosticLesson(session.lessonName)
        ? getNeedAssessmentLabelForMakeupDiagnostic(student.learningPathId, student.startingLessonId)
        : null;
      return (
        <>
          <span
            className="inline-flex flex-wrap items-center gap-1.5"
            data-testid={`feedback-row-cell-student-${rowNumber}`}
          >
            <span className="capitalize">{name}</span>
            <StudentNeedsIndicators
              specialEd={student.specialEd}
              dyslexic={student.dyslexic}
              ell={student.ell}
              mode="icons"
            />
            {label ? <span>{label}</span> : null}
          </span>
          {onDemandGLA && <OnDemandGLALink onDemandGLA={onDemandGLA} grade={student.grade} />}
        </>
      );
    },
  }),
  columnHelper.accessor('session.lessonName', {
    id: CoachViewableFeedbacksTableColumnIds.lessonName,
    header: 'Lesson',
    cell: ({ getValue, row }) => {
      const rowNumber = row.index + 1;
      return <span data-testid={`feedback-row-cell-lesson-${rowNumber}`}>{getValue()}</span>;
    },
  }),
  columnHelper.accessor('student.grade', {
    id: CoachViewableFeedbacksTableColumnIds.grade,
    header: 'Grade',
    cell: ({ getValue, row }) => {
      const rowNumber = row.index + 1;
      return (
        <span data-testid={`feedback-row-cell-grade-${rowNumber}`}>{studentYearOption(stringIfNull(getValue()))}</span>
      );
    },
  }),
  columnHelper.accessor('student', {
    id: CoachViewableFeedbacksTableColumnIds.schoolDistrict,
    header: 'District',
    cell: ({ getValue, row }) => {
      const rowNumber = row.index + 1;
      const student = getValue();

      return <span data-testid={`feedback-row-cell-school-district-${rowNumber}`}>{student.orgName || 'Unknown'}</span>;
    },
  }),
];

function CoachFeedbackDrawerTitle({
  feedback,
  timeZone,
  isFullPage,
  onToggleFullPage,
  onRequestClose,
}: {
  feedback: CoachViewableSessionFeedback;
  timeZone: string;
  isFullPage: boolean;
  onToggleFullPage?: () => void;
  onRequestClose: () => void;
}) {
  return (
    <div>
      {!isFullPage && (
        <div className="text-[30px] font-semibold mb-2 max-md:text-left flex items-center justify-between gap-2 max-md:text-2xl">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <span className="shrink-0 capitalize" data-testid="feedback-form-title">
              {feedback.student.coachFacingDisplayName}
            </span>
            <StudentNeedsIndicators
              specialEd={feedback.student.specialEd}
              dyslexic={feedback.student.dyslexic}
              ell={feedback.student.ell}
              mode="full-text"
            />
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {onToggleFullPage && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={onToggleFullPage}
                title={isFullPage ? 'Minimize' : 'Expand to full page'}
                className="h-8 w-8 p-0 max-md:hidden"
                data-testid="feedback-button-expand"
              >
                <Maximize2 className="h-4 w-4" />
              </Button>
            )}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={onRequestClose}
              title="Close"
              className="h-8 w-8 p-0"
              data-testid="feedback-button-close-drawer"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
        <div className="flex items-center">
          <ClockIcon size={16} className="mr-1 text-gray-400" />
          <span data-testid="feedback-form-value-session-time">
            {formatInstant(feedback.session.startInstant, timeZone).formattedDateWithoutYear},{' '}
            {formatTimeRange(feedback.session.startInstant, feedback.session.endInstant, timeZone)}
          </span>
        </div>
        <div className="flex items-center">
          <WallpaperIcon size={16} className="mr-1 text-gray-400" />
          <span data-testid="feedback-form-value-lesson">{feedback.session.lessonName}</span>
        </div>
      </div>
    </div>
  );
}

export const CoachViewableFeedbacksTable = <GroupKey extends BaseEntityTableGroupKey = BaseEntityTableGroupKey>({
  sessionFeedbacks,
  filters,
  filterBarClassName,
  filterLeftClassName,
  tableHookProps,
  emptyState,
  isLoading,
  showPagination = false,
  groupBy,
}: CoachViewableFeedbacksTableProps<GroupKey>) => {
  const { timeZone } = useTimeZone();
  const [selectedFeedback, setSelectedFeedback] = useState<CoachViewableSessionFeedback | null>(null);
  const [isFullPage, setIsFullPage] = useState(false);
  const saveAndCloseRef = useRef<SaveAndCloseHandle | null>(null);
  const showConfirmationRef = useRef<(() => void) | null>(null);
  const setShouldConfirmOnCloseRef = useRef<((value: boolean) => void) | null>(null);

  const handleSelectFeedback = useCallback((feedback: CoachViewableSessionFeedback) => {
    setSelectedFeedback(feedback);
    setIsFullPage(false);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    // Clean up localStorage when closing
    if (saveAndCloseRef.current?.cleanupOnClose) {
      saveAndCloseRef.current.cleanupOnClose();
    }
    setSelectedFeedback(null);
  }, []);

  const handleBeforeClose = useCallback(async () => {
    if (saveAndCloseRef.current) {
      const result = await saveAndCloseRef.current.saveAndClose();
      // If we need to show confirmation and can't close, show the Dialog immediately
      if (!result.canClose && result.showConfirmation && showConfirmationRef.current) {
        showConfirmationRef.current();
      }
      return result.canClose;
    }
    return true;
  }, []);

  const handleRequestClose = useCallback(() => {
    // Check if there are unsaved changes before closing
    if (saveAndCloseRef.current) {
      const hasUnsavedChanges = saveAndCloseRef.current.hasUnsavedChanges();
      if (hasUnsavedChanges) {
        // Set shouldConfirmOnClose to true and show confirmation dialog without saving
        if (setShouldConfirmOnCloseRef.current) {
          setShouldConfirmOnCloseRef.current(true);
        }
        if (showConfirmationRef.current) {
          showConfirmationRef.current();
        }
      } else {
        // No unsaved changes, close immediately
        handleCloseDrawer();
      }
    } else {
      // No ref available, close immediately
      handleCloseDrawer();
    }
  }, [handleCloseDrawer]);

  const columns = useMemo(
    () => createCoachViewableFeedbacksColumns(timeZone, handleSelectFeedback),
    [timeZone, handleSelectFeedback],
  );

  const table = useReactTable({
    data: sessionFeedbacks,
    columns,
    getRowId: (row) => toSessionFeedbackCompoundId(row),
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    defaultColumn: {
      enableSorting: false,
    },
    ...tableHookProps,
  });

  const renderFeedbackPanel = useCallback(
    (drawerProps: {
      closeDrawer: (bypassConfirmation?: boolean) => void;
      setShouldConfirmOnClose: (value: boolean) => void;
      showConfirmation: () => void;
    }) => {
      if (!selectedFeedback) {
        return null;
      }

      showConfirmationRef.current = drawerProps.showConfirmation;
      setShouldConfirmOnCloseRef.current = drawerProps.setShouldConfirmOnClose;

      return (
        <InfoPageSuspenseBoundary
          onError={(e) => {
            const axiosError = axiosErrorFrom(e);
            const airServerError = airServerErrorFrom(e);

            return {
              allowRetry: axiosError?.response?.status !== 404,
              errorHeader:
                airServerError?.userFacingMessage ||
                'Failed to load feedback form. Please try again or contact Air Reading support',
            };
          }}
        >
          <CoachViewableSessionFeedbackPage
            feedbackCompoundId={{
              coachId: selectedFeedback.coach.id,
              sessionId: selectedFeedback.session.id,
              studentId: selectedFeedback.student.id,
            }}
            suppressStudentTitleRow
            onClose={(bypassConfirmation) => {
              if (bypassConfirmation) {
                handleCloseDrawer();
              } else {
                drawerProps.closeDrawer();
                handleCloseDrawer();
              }
            }}
            setShouldConfirmOnClose={drawerProps.setShouldConfirmOnClose}
            isFullPage={isFullPage}
            onFullPageChange={setIsFullPage}
            saveAndCloseRef={saveAndCloseRef}
            showConfirmation={drawerProps.showConfirmation}
          />
        </InfoPageSuspenseBoundary>
      );
    },
    [handleCloseDrawer, isFullPage, selectedFeedback],
  );

  return (
    <>
      <div
        className={cn(
          'flex h-full min-h-0 flex-1 flex-col min-w-0',
          // Shrink edit column to the 48px control; keep other columns at natural content width (horizontal scroll).
          'max-md:[&_tbody_tr]:h-12',
          'max-md:[&_thead_th]:h-12',
          'max-md:[&_tbody_td:first-child]:w-px',
          'max-md:[&_tbody_td:first-child]:whitespace-nowrap',
          'max-md:[&_tbody_td:first-child]:p-0',
          'max-md:[&_thead_th:first-child]:w-px',
          'max-md:[&_thead_th:first-child]:whitespace-nowrap',
          'max-md:[&_thead_th:first-child]:p-0',
          'max-md:[&_tbody_td:not(:first-child)]:py-2',
          'max-md:[&_tbody_td:not(:first-child)]:px-3',
        )}
      >
        <EntityTable
          table={table}
          filters={filters}
          filterBarClassName={filterBarClassName}
          filterLeftClassName={filterLeftClassName}
          emptyState={emptyState}
          isLoading={isLoading}
          showPagination={showPagination}
          groupBy={groupBy}
          autoManagedColumnIds={[CoachViewableFeedbacksTableColumnIds.viewDetailsCoach]}
        />
      </div>
      {selectedFeedback && (
        <RightDrawer
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              handleCloseDrawer();
            }
          }}
          hideCloseButton={true}
          title={
            <CoachFeedbackDrawerTitle
              feedback={selectedFeedback}
              timeZone={timeZone}
              isFullPage={isFullPage}
              onToggleFullPage={() => setIsFullPage(!isFullPage)}
              onRequestClose={handleRequestClose}
            />
          }
          dismissible={false}
          onBeforeClose={handleBeforeClose}
          confirmationTitle="Unsaved changes"
          confirmationDescription="You have unsaved changes. Are you sure you want to close without saving?"
          confirmationConfirmText="Close without saving"
          confirmationCancelText="Stay"
          widthClass={cn(
            'bg-[#F7F7F9] w-[40%] min-w-[900px] max-w-[90%]',
            'max-md:inset-0 max-md:left-0 max-md:h-[100dvh] max-md:w-full max-md:min-w-0 max-md:max-w-none max-md:border-0',
            'max-[900px]:w-[100%] max-[900px]:min-w-0 max-[900px]:max-w-full max-[900px]:sm:max-w-full',
          )}
        >
          {({ closeDrawer, setShouldConfirmOnClose, showConfirmation }) =>
            renderFeedbackPanel({ closeDrawer, setShouldConfirmOnClose, showConfirmation })
          }
        </RightDrawer>
      )}
    </>
  );
};
